import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import InsightsIcon from '@mui/icons-material/Insights';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const featureCards = [
  {
    icon: <SmartphoneIcon sx={{ fontSize: 36 }} />,
    title: 'Mobile App Control',
    desc: 'Manage all your pool tables remotely from anywhere using our intuitive mobile application.',
  },
  {
    icon: <CreditCardIcon sx={{ fontSize: 36 }} />,
    title: 'In-App Payments',
    desc: 'Clients pay directly through the app — no cash, no coins, no awkward key handoffs.',
  },
  {
    icon: <LockOpenIcon sx={{ fontSize: 36 }} />,
    title: 'Automated Unlocking',
    desc: 'Tables unlock automatically the moment payment is confirmed, for the exact number of rounds purchased.',
  },
  {
    icon: <InsightsIcon sx={{ fontSize: 36 }} />,
    title: 'Real-Time Analytics',
    desc: 'Track revenue, game sessions, and device status live — all in one dashboard.',
  },
  {
    icon: <WifiOffIcon sx={{ fontSize: 36 }} />,
    title: 'No Staff Required',
    desc: 'Eliminate the overhead of hiring dedicated attendants. The system manages itself.',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 36 }} />,
    title: '24/7 Support',
    desc: 'Our team is always on standby to help you get the most out of SpotPool.',
  },
];

const About = () => {
  return (
    <Box id='about' sx={{ py: { xs: 8, md: 14 }, px: { xs: 3, md: 10 }, bgcolor: '#f9fafb' }}>

      {/* Section Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
        <Typography
          data-aos="fade-down"
          sx={{ color: 'primary.main', fontWeight: 700, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', mb: 1 }}
        >
          About SpotPool
        </Typography>
        <Typography
          variant='h3'
          data-aos="fade-up"
          sx={{ fontWeight: 800, color: '#0d1117', fontSize: { xs: '1.8rem', md: '2.6rem' }, mb: 2 }}
        >
          Everything You Need to Run a<br />
          <Box component='span' sx={{ color: 'primary.main' }}>Modern Pool Hall</Box>
        </Typography>
        <Typography data-aos="fade-up" data-aos-delay="100" sx={{ color: '#6b7280', maxWidth: 600, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.8 }}>
          SpotPool replaces outdated coin and key systems with a fully digital, automated solution that puts you in complete control — from anywhere.
        </Typography>
      </Box>

      {/* Feature Cards */}
      <Grid container spacing={3} sx={{ mb: { xs: 8, md: 12 } }}>
        {featureCards.map(({ icon, title, desc }, i) => (
          <Grid item xs={12} sm={6} md={4} key={title}>
            <Paper
              data-aos="fade-up"
              data-aos-delay={i * 80}
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 4,
                border: '1px solid #e5e7eb',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 20px 40px rgba(0,107,70,0.12)',
                  borderColor: 'primary.main',
                }
              }}
            >
              <Box sx={{ color: 'primary.main', mb: 2 }}>{icon}</Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1, color: '#0d1117' }}>{title}</Typography>
              <Typography sx={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: 1.7 }}>{desc}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Demo Video */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 8, alignItems: 'center' }}>
        <Box sx={{ flex: 1 }} data-aos="fade-right">
          <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', mb: 1 }}>
            See It In Action
          </Typography>
          <Typography variant='h4' sx={{ fontWeight: 800, color: '#0d1117', mb: 2, fontSize: { xs: '1.6rem', md: '2rem' } }}>
            Watch How SpotPool Works
          </Typography>
          <Typography sx={{ color: '#6b7280', lineHeight: 1.9, fontSize: '1rem' }}>
            Welcome to the future of pool table management! At SpotPool, we are revolutionizing the way pool table owners operate their businesses. Say goodbye to the hassle of hiring staff and the outdated coin or key systems.
            <br /><br />
            Our innovative automated solution puts you in complete control — effortlessly manage tables, collect payments, and monitor performance all from your phone.
          </Typography>
        </Box>
        <Box
          data-aos="fade-left"
          sx={{
            flex: 1,
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 30px 60px rgba(0,0,0,0.15)',
            aspectRatio: '16/9',
            '& iframe': { width: '100%', height: '100%', display: 'block' }
          }}
          dangerouslySetInnerHTML={{
            __html: `
              <iframe
                src="https://www.youtube.com/embed/xbeM7etoQL4?si=qftKdolI4cH4L5eP"
                title="SpotPool Demo"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
                style="width:100%;height:100%">
              </iframe>
            `
          }}
        />
      </Box>
    </Box>
  );
};

export default About;
